import {getTestProps} from '@benchling/js-test-utils';
import {
  Alert,
  Button,
  CopyButton,
  createStyles,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import React, {useState} from 'react';

import {getUserMessage, isJqXHR} from 'util/Net';

import {DataSharingConnectionInfo as ConnectionInfo, fetchDataSharingConnectionInfo} from './WarehouseDAO';

const COMPONENT = 'DataSharingConnectionInfo';

// Snowflake is only reachable over HTTPS.
const SNOWFLAKE_PORT = '443';

const useStyles = createStyles((theme) => ({
  root: {
    marginBottom: theme.spacing.sm,
  },
  tokenInput: {
    flex: 1,
  },
}));

type FetchState =
  | {status: 'idle'}
  | {status: 'loading'}
  | {status: 'loaded'; connectionInfo: ConnectionInfo}
  | {status: 'failed'; notSetUp: boolean; message: string};

function selectAll(e: React.FocusEvent<HTMLInputElement>): void {
  e.currentTarget.select();
}

function ReadOnlyField({
  label,
  value,
  element,
}: {
  label: string;
  value: string;
  element: string;
}): JSX.Element {
  return (
    <TextInput
      label={label}
      value={value}
      readOnly={true}
      onFocus={selectAll}
      {...getTestProps({component: COMPONENT, element})}
    />
  );
}

function AccessTokenField({token}: {token: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Group spacing="xs" align="flex-end" noWrap={true}>
      <PasswordInput
        label="Programmatic access token (password)"
        value={token}
        readOnly={true}
        onFocus={selectAll}
        className={classes.tokenInput}
        {...getTestProps({component: COMPONENT, element: 'accessTokenInput'})}
      />
      <CopyButton value={token}>
        {(clipboard) => (
          <Button
            variant="secondary"
            onClick={() => {
              clipboard.copy();
            }}
            {...getTestProps({component: COMPONENT, element: 'copyTokenButton'})}
          >
            {clipboard.copied ? 'Copied' : 'Copy'}
          </Button>
        )}
      </CopyButton>
    </Group>
  );
}

function ConnectionDetails({connectionInfo}: {connectionInfo: ConnectionInfo}): JSX.Element {
  return (
    <Stack spacing="sm" {...getTestProps({component: COMPONENT, element: 'details'})}>
      <Alert variant="warning">
        This token can read all of this tenant&apos;s shared data, regardless of individual user permissions.
        Store it securely and share it only with the systems that need it.
      </Alert>
      <Title order={4}>Snowflake connection</Title>
      <ReadOnlyField label="Host" value={connectionInfo.host} element="hostInput" />
      <ReadOnlyField label="Port" value={SNOWFLAKE_PORT} element="portInput" />
      <ReadOnlyField label="User" value={connectionInfo.readerUser} element="userInput" />
      <AccessTokenField token={connectionInfo.programmaticAccessToken} />
      <ReadOnlyField label="Role" value={connectionInfo.readRole} element="roleInput" />
      <ReadOnlyField label="Warehouse" value={connectionInfo.readerWarehouse} element="warehouseInput" />
      <ReadOnlyField label="Database" value={connectionInfo.catalogName} element="databaseInput" />
      <ReadOnlyField
        label="Shared schema"
        value={connectionInfo.sharedNamespace}
        element="sharedSchemaInput"
      />
      <Title order={4}>Iceberg REST catalog</Title>
      <ReadOnlyField label="Catalog URI" value={connectionInfo.catalogUri} element="catalogUriInput" />
      <ReadOnlyField label="Catalog name" value={connectionInfo.catalogName} element="catalogNameInput" />
      <ReadOnlyField label="Namespace" value={connectionInfo.namespace} element="namespaceInput" />
      <ReadOnlyField
        label="OAuth2 token endpoint"
        value={connectionInfo.tokenEndpoint}
        element="tokenEndpointInput"
      />
    </Stack>
  );
}

/**
 * Shows the Snowflake connection details, including the programmatic access token, that an external
 * consumer of the tenant's outbound data share (e.g. Databricks) uses to connect. The details are
 * only fetched when the user asks for them, since every fetch reads the tenant-wide token.
 */
export default function DataSharingConnectionInfo(): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const [fetchState, setFetchState] = useState<FetchState>({status: 'idle'});

  const handleShow = (): void => {
    setFetchState({status: 'loading'});
    fetchDataSharingConnectionInfo().then(
      (connectionInfo) => {
        setFetchState({status: 'loaded', connectionInfo});
      },
      (e: unknown) => {
        setFetchState({
          status: 'failed',
          notSetUp: isJqXHR(e) && e.status === 404,
          message: getUserMessage(e) ?? 'Failed to load data sharing connection details.',
        });
      }
    );
  };

  return (
    <Stack spacing="sm" className={classes.root}>
      <Title order={2}>Data Sharing Connection Details</Title>
      <Text color="dimmed">
        Snowflake connection details for reading this tenant&apos;s shared data from an external platform such
        as Databricks.
      </Text>
      {(fetchState.status === 'idle' || fetchState.status === 'loading') && (
        <Group>
          <Button
            variant="secondary"
            size="xs"
            loading={fetchState.status === 'loading'}
            onClick={handleShow}
            {...getTestProps({component: COMPONENT, element: 'showButton'})}
          >
            Show Connection Details
          </Button>
        </Group>
      )}
      {fetchState.status === 'failed' && (
        <Alert
          variant={fetchState.notSetUp ? 'info' : 'error'}
          {...getTestProps({component: COMPONENT, element: 'error'})}
        >
          {fetchState.message}
        </Alert>
      )}
      {fetchState.status === 'loaded' && <ConnectionDetails connectionInfo={fetchState.connectionInfo} />}
    </Stack>
  );
}
